import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  ContentCopy,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import APIKit from '../../../config/APIKit';
import { showSnackBar } from '../../store/compatibility';
import { useDispatch } from 'react-redux';
import style from '../settings/settings.module.scss';
import Title from '../settings/Title';
import ConfirmationDialog from '../../component/ConfirmationDialog';

export default function APIKeyManagement() {
  const dispatch = useDispatch();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, key: null, mode: 'create' });
  const [selectedKey, setSelectedKey] = useState(null);
  const [usage, setUsage] = useState([]);
  const [tab, setTab] = useState(0);
  const [newKey, setNewKey] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    type: 'delete',
    loading: false,
  });

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      const response = await APIKit.get('/api-keys');
      if (response.success) {
        setApiKeys(response.data || []);
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
      dispatch(showSnackBar({ message: 'Error loading API keys', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const loadUsage = async (keyId) => {
    try {
      const response = await APIKit.get(`/api-keys/${keyId}/usage`);
      if (response.success) {
        setUsage(response.data || []);
      }
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const handleCreate = () => {
    setModal({ open: true, key: null, mode: 'create' });
    setNewKey(null);
  };

  const handleEdit = (key) => {
    setModal({ open: true, key, mode: 'edit' });
  };

  const openConfirmDialog = (keyId, type = 'delete') => {
    setConfirmDialog({
      open: true,
      id: keyId,
      type,
      loading: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      id: null,
      type: 'delete',
      loading: false,
    });
  };

  const handleDelete = async () => {
    if (!confirmDialog.id) return;
    
    setConfirmDialog(prev => ({
      ...prev,
      loading: true,
    }));

    try {
      await APIKit.delete(`/api-keys/${confirmDialog.id}`);
      dispatch(showSnackBar({ message: 'API key deleted successfully', severity: 'success' }));
      loadAPIKeys();
      closeConfirmDialog();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error deleting API key', severity: 'error' }));
      setConfirmDialog(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleRevoke = async () => {
    if (!confirmDialog.id) return;
    
    setConfirmDialog(prev => ({
      ...prev,
      loading: true,
    }));

    try {
      await APIKit.post(`/api-keys/${confirmDialog.id}/revoke`);
      dispatch(showSnackBar({ message: 'API key revoked successfully', severity: 'success' }));
      loadAPIKeys();
      closeConfirmDialog();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error revoking API key', severity: 'error' }));
      setConfirmDialog(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleViewUsage = (key) => {
    setSelectedKey(key);
    setTab(1);
    loadUsage(key.id);
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    dispatch(showSnackBar({ message: 'API key copied to clipboard', severity: 'success' }));
  };

  return (
    <div className={style.container}>
      <Title
        title="API Key Management"
        handleModal={handleCreate}
        enableButton={true}
      />

      {newKey && (
        <Alert
          severity="success"
          sx={{
            mt: 2,
            mb: 2,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            API Key Created Successfully!
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, color: '#666' }}>
            <strong>Important:</strong> Save this key now. It will not be shown again.
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <TextField
              value={newKey}
              fullWidth
              disabled
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <IconButton
              onClick={() => handleCopyKey(newKey)}
              sx={{
                background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                color: '#00a78e',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                  color: '#ffffff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 167, 142, 0.3)',
                },
              }}
            >
              <ContentCopy />
            </IconButton>
          </Box>
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          sx={{
            mb: 3,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
              height: '3px',
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            label="API Keys"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              '&.Mui-selected': {
                color: '#00a78e',
              },
            }}
          />
          {selectedKey && (
            <Tab
              label={`Usage: ${selectedKey.name}`}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '15px',
                '&.Mui-selected': {
                  color: '#00a78e',
                },
              }}
            />
          )}
        </Tabs>

        {tab === 0 && (
          <Box
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 167, 142, 0.1)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a78e 0%, #0f766e 50%, #f093fb 100%)',
                zIndex: 1,
              },
            }}
          >
            <TableContainer sx={{ px: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 3, fontWeight: 700, color: '#00a78e' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Key Prefix</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Permissions</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Rate Limit</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Usage</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Expires</TableCell>
                    <TableCell align="right" sx={{ pr: 3, fontWeight: 700, color: '#00a78e' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No API keys configured</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    apiKeys.map(key => (
                      <TableRow
                        key={key.id}
                        sx={{
                          '&:hover': {
                            background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.05) 0%, rgba(15, 118, 110, 0.05) 100%)',
                          },
                        }}
                      >
                        <TableCell sx={{ pl: 3 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {key.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
                            {key.key_prefix}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {key.permissions?.slice(0, 2).map(perm => (
                              <Chip
                                key={perm}
                                label={perm}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: 'rgba(0, 167, 142, 0.3)',
                                  color: '#00a78e',
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                            {key.permissions?.length > 2 && (
                              <Chip
                                label={`+${key.permissions.length - 2}`}
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                                  color: '#00a78e',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#666' }}>{key.rate_limit}/hour</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#666' }}>{key.usage_count || 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={key.is_active ? 'Active' : 'Revoked'}
                            size="small"
                            sx={{
                              background: key.is_active
                                ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                : 'rgba(0, 0, 0, 0.1)',
                              color: key.is_active ? '#ffffff' : '#666',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#666' }}>
                          {key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Tooltip title="View Usage">
                            <IconButton
                              size="small"
                              onClick={() => handleViewUsage(key)}
                              sx={{
                                background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                                color: '#00a78e',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                                  color: '#ffffff',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(0, 167, 142, 0.3)',
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(key)}
                            sx={{
                              background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                              color: '#00a78e',
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                                color: '#ffffff',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0, 167, 142, 0.3)',
                              },
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          {key.is_active && (
                            <Tooltip title="Revoke">
                              <IconButton
                                size="small"
                                onClick={() => openConfirmDialog(key.id, 'revoke')}
                                sx={{
                                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 111, 0, 0.1) 100%)',
                                  color: '#ff9800',
                                  borderRadius: '8px',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #ff9800 0%, #ff6f00 100%)',
                                    color: '#ffffff',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                                  },
                                }}
                              >
                                <Block fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => openConfirmDialog(key.id, 'delete')}
                            sx={{
                              background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(229, 57, 53, 0.1) 100%)',
                              color: '#ef5350',
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                                color: '#ffffff',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && selectedKey && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                API Key Usage: {selectedKey.name}
              </Typography>
              {usage.stats && (
                <Box>
                  <Chip
                    label={`Total: ${usage.stats.total_requests}`}
                    sx={{
                      mr: 1,
                      background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                      color: '#00a78e',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`Success: ${usage.stats.successful_requests}`}
                    sx={{
                      mr: 1,
                      background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`Failed: ${usage.stats.failed_requests}`}
                    sx={{
                      background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 167, 142, 0.1)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #00a78e 0%, #0f766e 50%, #f093fb 100%)',
                  zIndex: 1,
                },
              }}
            >
              <TableContainer sx={{ px: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 3, fontWeight: 700, color: '#00a78e' }}>Endpoint</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>IP Address</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#00a78e' }}>Response Time</TableCell>
                      <TableCell sx={{ pr: 3, fontWeight: 700, color: '#00a78e' }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usage.data && usage.data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">No usage data yet</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      usage.data?.map((usageItem, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:hover': {
                              background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.05) 0%, rgba(15, 118, 110, 0.05) 100%)',
                            },
                          }}
                        >
                          <TableCell sx={{ pl: 3 }}>{usageItem.endpoint}</TableCell>
                          <TableCell>
                            <Chip
                              label={usageItem.method}
                              size="small"
                              sx={{
                                background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                                color: '#00a78e',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#666' }}>{usageItem.ip_address || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={usageItem.response_status}
                              size="small"
                              sx={{
                                background: usageItem.response_status >= 200 && usageItem.response_status < 300
                                  ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                  : 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                                color: '#ffffff',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#666' }}>{usageItem.response_time_ms}ms</TableCell>
                          <TableCell sx={{ pr: 3, color: '#666' }}>
                            {new Date(usageItem.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        open={confirmDialog.open && confirmDialog.type === 'delete'}
        onClose={closeConfirmDialog}
        onConfirm={handleDelete}
        title="Delete API Key?"
        message="Are you sure you want to delete this API key? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
        confirmColor="error"
        loading={confirmDialog.loading}
      />

      <ConfirmationDialog
        open={confirmDialog.open && confirmDialog.type === 'revoke'}
        onClose={closeConfirmDialog}
        onConfirm={handleRevoke}
        title="Revoke API Key?"
        message="This will disable the API key. You can reactivate it later if needed."
        confirmText="Revoke"
        cancelText="Cancel"
        type="warning"
        confirmColor="warning"
        loading={confirmDialog.loading}
      />

      <APIKeyModal
        open={modal.open}
        key={modal.key}
        mode={modal.mode}
        onClose={() => {
          setModal({ open: false, key: null, mode: 'create' });
          setNewKey(null);
        }}
        onSave={(createdKey) => {
          if (createdKey) {
            setNewKey(createdKey);
          }
          loadAPIKeys();
        }}
      />
    </div>
  );
}

// API Key Create/Edit Modal
function APIKeyModal({ open, key: apiKey, mode, onClose, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    allowed_ips: [],
    rate_limit: 1000,
    expires_at: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [ipInput, setIpInput] = useState('');

  useEffect(() => {
    if (apiKey) {
      setFormData({
        name: apiKey.name || '',
        description: apiKey.description || '',
        permissions: apiKey.permissions || [],
        allowed_ips: apiKey.allowed_ips || [],
        rate_limit: apiKey.rate_limit || 1000,
        expires_at: apiKey.expires_at ? apiKey.expires_at.split('T')[0] : '',
        is_active: apiKey.is_active !== undefined ? apiKey.is_active : true,
      });
    }
  }, [apiKey]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        expires_at: formData.expires_at || null,
      };

      const response = mode === 'create'
        ? await APIKit.post('/api-keys', payload)
        : await APIKit.post(`/api-keys/${apiKey.id}`, payload);

      if (response.success) {
        if (mode === 'create' && response.data.key) {
          onSave(response.data.key);
          dispatch(showSnackBar({
            message: 'API key created! Save it now - it won\'t be shown again.',
            severity: 'success',
          }));
        } else {
          dispatch(showSnackBar({ message: 'API key updated successfully', severity: 'success' }));
          onSave();
        }
        onClose();
      }
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error saving API key', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddIP = () => {
    if (ipInput && !formData.allowed_ips.includes(ipInput)) {
      setFormData({
        ...formData,
        allowed_ips: [...formData.allowed_ips, ipInput],
      });
      setIpInput('');
    }
  };

  const handleRemoveIP = (ip) => {
    setFormData({
      ...formData,
      allowed_ips: formData.allowed_ips.filter(i => i !== ip),
    });
  };

  const availablePermissions = [
    'read:clients',
    'write:clients',
    'read:invoices',
    'write:invoices',
    'read:expenses',
    'write:expenses',
    'read:projects',
    'write:projects',
    'read:leads',
    'write:leads',
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '20px',
          py: 2.5,
          px: 3,
          mb: 0,
        }}
      >
        {mode === 'create' ? 'Create API Key' : 'Edit API Key'}
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 3, overflow: 'visible' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <FormControl fullWidth>
              <InputLabel
                id="permissions-label"
                sx={{
                  '&.MuiInputLabel-root': {
                    backgroundColor: 'transparent',
                    px: 0.5,
                    transform: 'translate(14px, 16px) scale(1)',
                  },
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                }}
              >
                Permissions
              </InputLabel>
              <Select
                labelId="permissions-label"
                multiple
                value={formData.permissions}
                onChange={(e) => setFormData({ ...formData, permissions: e.target.value })}
                label="Permissions"
                sx={{
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '12px',
                  },
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availablePermissions.map(perm => (
                  <MenuItem key={perm} value={perm}>
                    {perm}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, color: '#00a78e' }}>
              Allowed IPs (leave empty for all IPs)
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                label="IP Address"
                value={ipInput}
                onChange={(e) => setIpInput(e.target.value)}
                placeholder="192.168.1.1"
                size="small"
                onKeyPress={(e) => e.key === 'Enter' && handleAddIP()}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <Button
                onClick={handleAddIP}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#00a78e',
                  color: '#00a78e',
                  '&:hover': {
                    borderColor: '#0f766e',
                    background: 'rgba(0, 167, 142, 0.1)',
                  },
                }}
              >
                Add
              </Button>
            </Box>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {formData.allowed_ips.map(ip => (
                <Chip
                  key={ip}
                  label={ip}
                  onDelete={() => handleRemoveIP(ip)}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                    color: '#00a78e',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Rate Limit (requests per hour)"
              type="number"
              value={formData.rate_limit}
              onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
              fullWidth
              inputProps={{ min: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Expires At"
              type="date"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#00a78e',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#00a78e',
                    },
                  }}
                />
              }
              label="Active"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontWeight: 600,
                  color: '#00a78e',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.4)',
              background: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
            boxShadow: '0 4px 16px rgba(0, 167, 142, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f766e 0%, #00a78e 100%)',
              boxShadow: '0 6px 20px rgba(0, 167, 142, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: 'rgba(0, 167, 142, 0.5)',
            },
          }}
        >
          {loading
            ? mode === 'create'
              ? 'Creating...'
              : 'Updating...'
            : mode === 'create'
            ? 'Create'
            : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
